/**
 * Segment Personalization Configuration
 * Vision 2.0 Phase 2: Customize the experience based on user segment selection
 *
 * Segments:
 * - no-degree: Emphasize skills over credentials
 * - career-changer: Focus on transferable skills
 * - passion-seeker: Add passion-mapping focus
 * - ai-displaced: Highlight AI-resistant careers
 * - gig-seeker: Show freelance pathways prominently
 * - returning: Address career gaps positively
 */

export type UserSegment =
  | 'no-degree'
  | 'career-changer'
  | 'passion-seeker'
  | 'ai-displaced'
  | 'gig-seeker'
  | 'returning'
  | null;

export interface SegmentConfig {
  id: UserSegment;
  // Results page customization
  resultsHeadline: string;
  resultsSubheadline: string;
  // Career emphasis keywords - careers with these get boosted
  priorityKeywords: string[];
  // Categories to highlight
  priorityCategories: string[];
  // Match reason prefixes to make messaging more personal
  matchReasonPrefix: string;
  // Encouragement message for results
  encouragementMessage: string;
  // Call-to-action text for premium
  premiumCtaText: string;
  // Question customizations
  questionOverrides: {
    [questionId: string]: {
      encouragementText?: string;
      headerNote?: string;
    };
  };
  // Filter preferences
  showFreelancePathways: boolean;
  emphasizeRemote: boolean;
  emphasizeQuickHire: boolean;
  emphasizeAIResistant: boolean;
  emphasizeNoDegree: boolean;
  // Career filtering tags
  careerBoostTags: string[];
}

/**
 * Default configuration for no segment selected
 */
const DEFAULT_CONFIG: SegmentConfig = {
  id: null,
  resultsHeadline: 'Your Career Matches',
  resultsSubheadline: 'Based on your skills and preferences',
  priorityKeywords: [],
  priorityCategories: [],
  matchReasonPrefix: '',
  encouragementMessage: 'These careers match your profile. Explore them to find your next step.',
  premiumCtaText: 'Get Your Personalized Career Blueprint',
  questionOverrides: {},
  showFreelancePathways: false,
  emphasizeRemote: false,
  emphasizeQuickHire: false,
  emphasizeAIResistant: false,
  emphasizeNoDegree: true,
  careerBoostTags: [],
};

/**
 * Segment-specific configurations
 */
export const SEGMENT_CONFIGS: Record<string, SegmentConfig> = {
  'no-degree': {
    id: 'no-degree',
    resultsHeadline: 'Careers That Value Skills Over Credentials',
    resultsSubheadline: 'No degree? No problem. These roles value what you CAN do.',
    priorityKeywords: ['no_degree', 'skills_based', 'apprenticeship', 'certification', 'hands_on'],
    priorityCategories: ['Trades & Skilled Labor', 'Technical & IT Support', 'Operations & Logistics'],
    matchReasonPrefix: 'Skills-based: ',
    encouragementMessage: 'Every career here is achievable without a college degree. Your skills and drive are what matter most.',
    premiumCtaText: 'Get Your No-Degree Career Roadmap',
    questionOverrides: {
      'q1_core_capability': {
        headerNote: 'Your real-world skills matter more than degrees here.',
      },
      'q4_natural_strengths': {
        encouragementText: 'These strengths are exactly what employers value—often more than formal education!',
      },
      'q4b_existing_certifications': {
        encouragementText: 'Certifications can fast-track you into great roles. No degree needed when you have proof of skills!',
      },
    },
    showFreelancePathways: false,
    emphasizeRemote: false,
    emphasizeQuickHire: true,
    emphasizeAIResistant: false,
    emphasizeNoDegree: true,
    careerBoostTags: ['entry_level', 'no_experience', 'on_job_training'],
  },

  'career-changer': {
    id: 'career-changer',
    resultsHeadline: 'Fresh Start, Same Great Skills',
    resultsSubheadline: 'Your experience transfers further than you think',
    priorityKeywords: ['transferable_skills', 'career_change', 'cross_functional', 'versatile'],
    priorityCategories: ['Customer Service & Support', 'Administrative & Coordination', 'Sales & Business Development'],
    matchReasonPrefix: 'Transferable: ',
    encouragementMessage: 'Your past experience has built skills that translate directly to these new careers. Change is possible.',
    premiumCtaText: 'Map Your Career Transition Path',
    questionOverrides: {
      'q1_core_capability': {
        headerNote: 'Your skills from any industry transfer to new careers.',
      },
      'q3b_leadership_experience': {
        encouragementText: 'Leadership experience from ANY field opens doors to management roles in your new career!',
      },
      'q6_current_situation': {
        headerNote: 'Many successful career changers started exactly where you are now.',
      },
    },
    showFreelancePathways: true,
    emphasizeRemote: true,
    emphasizeQuickHire: false,
    emphasizeAIResistant: false,
    emphasizeNoDegree: false,
    careerBoostTags: ['mid_career', 'transferable', 'any_background'],
  },

  'passion-seeker': {
    id: 'passion-seeker',
    resultsHeadline: 'Turn What You Love Into What You Do',
    resultsSubheadline: 'Careers that align with your passions',
    priorityKeywords: ['creative', 'passion', 'artistic', 'music', 'gaming', 'sports', 'entertainment'],
    priorityCategories: ['Creative & Media', 'Entertainment & Sports', 'Education & Training'],
    matchReasonPrefix: 'Passion-aligned: ',
    encouragementMessage: 'Life is too short for work you hate. These careers let you do what you love AND get paid.',
    premiumCtaText: 'Design Your Passion-to-Paycheck Plan',
    questionOverrides: {
      'q1_core_capability': {
        headerNote: 'Think about what you love doing—even in your free time.',
      },
      'q4_natural_strengths': {
        encouragementText: 'Your natural talents often point to your passions. Trust what comes easily to you!',
      },
      'q10_priorities': {
        headerNote: 'Doing meaningful work matters—let\'s find your calling.',
      },
    },
    showFreelancePathways: true,
    emphasizeRemote: true,
    emphasizeQuickHire: false,
    emphasizeAIResistant: false,
    emphasizeNoDegree: false,
    careerBoostTags: ['creative', 'passion_project', 'side_hustle', 'artistic'],
  },

  'ai-displaced': {
    id: 'ai-displaced',
    resultsHeadline: 'AI-Proof Careers for the Future',
    resultsSubheadline: 'Roles that grow WITH technology, not against it',
    priorityKeywords: ['ai_resistant', 'human_essential', 'relationship', 'physical', 'creative', 'judgment'],
    priorityCategories: ['Healthcare & Social Services', 'Trades & Skilled Labor', 'Creative & Media'],
    matchReasonPrefix: 'AI-resistant: ',
    encouragementMessage: 'These careers require human judgment, creativity, and physical presence—skills AI can\'t replicate.',
    premiumCtaText: 'Future-Proof Your Career Path',
    questionOverrides: {
      'q1_core_capability': {
        headerNote: 'Skills that require human touch are your superpower in the AI age.',
      },
      'q9_timeline_aspiration': {
        encouragementText: 'Long-term thinking pays off. These careers will still be essential in 10, 20, even 30 years.',
      },
    },
    showFreelancePathways: false,
    emphasizeRemote: false,
    emphasizeQuickHire: false,
    emphasizeAIResistant: true,
    emphasizeNoDegree: false,
    careerBoostTags: ['ai_resistant', 'human_essential', 'cannot_automate', 'physical_presence'],
  },

  'gig-seeker': {
    id: 'gig-seeker',
    resultsHeadline: 'Be Your Own Boss',
    resultsSubheadline: 'Careers with freelance & side hustle potential',
    priorityKeywords: ['freelance', 'gig', 'self_employed', 'contractor', 'flexible', 'side_hustle'],
    priorityCategories: ['Creative & Media', 'Technical & IT Support', 'Trades & Skilled Labor'],
    matchReasonPrefix: 'Gig-ready: ',
    encouragementMessage: 'These careers offer freelance potential. Start building your client base on your own terms.',
    premiumCtaText: 'Launch Your Freelance Empire',
    questionOverrides: {
      'q3_work_style': {
        headerNote: 'Freelancing requires self-direction. Choose what fits YOUR style.',
      },
      'q8_location': {
        encouragementText: 'Freelancers can work from anywhere! Location flexibility is a huge perk.',
      },
      'q10_priorities': {
        headerNote: 'Freedom and flexibility are within reach.',
      },
    },
    showFreelancePathways: true,
    emphasizeRemote: true,
    emphasizeQuickHire: false,
    emphasizeAIResistant: false,
    emphasizeNoDegree: false,
    careerBoostTags: ['freelance_viable', 'gig_platforms', 'self_employed', 'contract_work'],
  },

  'returning': {
    id: 'returning',
    resultsHeadline: 'Welcome Back to the Workforce',
    resultsSubheadline: 'Your experience counts—career gaps don\'t define you',
    priorityKeywords: ['returner_friendly', 'flexible', 'training_provided', 'supportive'],
    priorityCategories: ['Administrative & Coordination', 'Customer Service & Support', 'Healthcare & Social Services'],
    matchReasonPrefix: 'Returner-friendly: ',
    encouragementMessage: 'Time away from work isn\'t wasted—caregiving, learning, and life experience all build valuable skills.',
    premiumCtaText: 'Plan Your Successful Return',
    questionOverrides: {
      'q1_core_capability': {
        headerNote: 'Skills from caregiving, volunteering, and life experience count here.',
      },
      'q4_natural_strengths': {
        encouragementText: 'The skills you\'ve developed during your time away are incredibly valuable to employers!',
      },
      'q6_current_situation': {
        headerNote: 'Many employers actively recruit people returning to the workforce.',
      },
      'q8_location': {
        encouragementText: 'Many returner-friendly roles offer flexibility to balance work and life.',
      },
    },
    showFreelancePathways: false,
    emphasizeRemote: true,
    emphasizeQuickHire: true,
    emphasizeAIResistant: false,
    emphasizeNoDegree: true,
    careerBoostTags: ['returner_program', 'flexible_hours', 'reentry_friendly', 'parental_support'],
  },
};

/**
 * Get the configuration for a user segment
 */
export function getSegmentConfig(segment: string | null | undefined): SegmentConfig {
  if (!segment || !SEGMENT_CONFIGS[segment]) {
    return DEFAULT_CONFIG;
  }
  return SEGMENT_CONFIGS[segment];
}

/**
 * Get question override for a specific question in a segment
 */
export function getQuestionOverride(
  segment: string | null | undefined,
  questionId: string
): { encouragementText?: string; headerNote?: string } | null {
  const config = getSegmentConfig(segment);
  return config.questionOverrides[questionId] || null;
}

/**
 * Calculate career score boost based on segment
 * Returns a multiplier (1.0 = no boost, 1.1 = 10% boost, etc.)
 */
export function getCareerSegmentBoost(
  segment: string | null | undefined,
  career: {
    keywords?: string[];
    category?: string;
    tags?: string[];
  }
): number {
  const config = getSegmentConfig(segment);
  if (!segment) return 1.0;

  let boost = 1.0;

  // Check priority keywords
  const careerKeywords = career.keywords || [];
  const matchingKeywords = careerKeywords.filter(k =>
    config.priorityKeywords.some(pk =>
      k.toLowerCase().includes(pk.toLowerCase()) ||
      pk.toLowerCase().includes(k.toLowerCase())
    )
  );
  if (matchingKeywords.length > 0) {
    boost += 0.05 * Math.min(matchingKeywords.length, 3); // Up to 15% boost
  }

  // Check priority categories
  if (career.category && config.priorityCategories.includes(career.category)) {
    boost += 0.1; // 10% boost for matching category
  }

  // Check career boost tags
  const careerTags = career.tags || [];
  const matchingTags = careerTags.filter(t =>
    config.careerBoostTags.some(bt =>
      t.toLowerCase().includes(bt.toLowerCase()) ||
      bt.toLowerCase().includes(t.toLowerCase())
    )
  );
  if (matchingTags.length > 0) {
    boost += 0.05 * Math.min(matchingTags.length, 2); // Up to 10% boost
  }

  return boost;
}

/**
 * Generate segment-specific match reasons
 */
export function enhanceMatchReasons(
  segment: string | null | undefined,
  originalReasons: string[],
  career: {
    keywords?: string[];
    category?: string;
    tags?: string[];
  }
): string[] {
  const config = getSegmentConfig(segment);
  if (!segment) return originalReasons;

  const enhancedReasons: string[] = [];

  // Add segment-specific reason if career matches priorities
  const careerKeywords = career.keywords || [];
  const hasMatchingKeyword = careerKeywords.some(k =>
    config.priorityKeywords.some(pk =>
      k.toLowerCase().includes(pk.toLowerCase())
    )
  );

  if (hasMatchingKeyword || (career.category && config.priorityCategories.includes(career.category))) {
    // Add segment-specific match reason
    switch (config.id) {
      case 'no-degree':
        enhancedReasons.push('No college degree required - values demonstrated skills');
        break;
      case 'career-changer':
        enhancedReasons.push('Great fit for career changers - values transferable experience');
        break;
      case 'passion-seeker':
        enhancedReasons.push('Aligns with creative/passion-driven work');
        break;
      case 'ai-displaced':
        enhancedReasons.push('AI-resistant role - requires human judgment and presence');
        break;
      case 'gig-seeker':
        enhancedReasons.push('Strong freelance/side hustle potential');
        break;
      case 'returning':
        enhancedReasons.push('Returner-friendly - values life experience');
        break;
    }
  }

  return [...enhancedReasons, ...originalReasons];
}

/**
 * Get segment-specific result summary text
 */
export function getResultsSummary(
  segment: string | null | undefined,
  topCareerTitle: string,
  matchScore: number
): string {
  const config = getSegmentConfig(segment);

  const scoreDescriptor = matchScore >= 85
    ? 'excellent'
    : matchScore >= 70
      ? 'strong'
      : matchScore >= 55
        ? 'solid'
        : 'potential';

  const baseText = `${topCareerTitle} is a ${scoreDescriptor} match for you!`;

  switch (config.id) {
    case 'no-degree':
      return `${baseText} This role values your skills and experience—no degree required.`;
    case 'career-changer':
      return `${baseText} Your transferable skills position you well for this transition.`;
    case 'passion-seeker':
      return `${baseText} This career aligns with what you love to do.`;
    case 'ai-displaced':
      return `${baseText} This role is resistant to AI automation—your human skills are essential.`;
    case 'gig-seeker':
      return `${baseText} This career has strong freelance and side-hustle potential.`;
    case 'returning':
      return `${baseText} This role is returner-friendly and values your life experience.`;
    default:
      return baseText;
  }
}
